/**
 * lotusBreathe.js
 *
 * Shared canvas 2D lotus breath visualiser engine.
 * Used by Protocol.jsx (full player) and Landing.jsx (hero mockup).
 *
 * 5 petals at 72° intervals, teardrop bezier curves.
 * Inhale = petals open outward. Exhale = petals close to centre.
 * Nadi Shodhana: left petals (3,4), right petals (1,2), shared petal (0).
 */

export const N_PETALS   = 5;
export const BASE_ANGLE = -Math.PI / 2;
export const CLOSED_LEN = 10, OPEN_LEN = 68;
export const CLOSED_W   = 2,  OPEN_W   = 20;
export const NADI_LEFT  = new Set([3, 4]);
export const NADI_RIGHT = new Set([1, 2]);

export function easeInOut(t) {
  return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2;
}

export function drawPetal(ctx, length, width, opacity, glowAmount, color) {
  ctx.save();
  ctx.globalAlpha = opacity;
  if (glowAmount > 0.1) { ctx.shadowColor = color; ctx.shadowBlur = glowAmount * 18; }
  const cp1x = length*0.35, cp1y = -width*0.9;
  const cp2x = length*0.72, cp2y = -width;
  const cp3x = length*0.72, cp3y =  width;
  const cp4x = length*0.35, cp4y =  width*0.9;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, length, 0);
  ctx.bezierCurveTo(cp3x, cp3y, cp4x, cp4y, 0, 0);
  ctx.closePath();
  const r = parseInt(color.slice(1,3),16);
  const g = parseInt(color.slice(3,5),16);
  const b = parseInt(color.slice(5,7),16);
  const grad = ctx.createLinearGradient(0, 0, length, 0);
  grad.addColorStop(0,   `rgba(${r},${g},${b},0)`);
  grad.addColorStop(0.3, `rgba(${r},${g},${b},${(opacity*0.18).toFixed(3)})`);
  grad.addColorStop(1,   `rgba(${r},${g},${b},${(opacity*0.08).toFixed(3)})`);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(cp1x*0.7, cp1y*0.5, cp2x*0.7, cp2y*0.4, length*0.85, 0);
  ctx.strokeStyle = `rgba(${r},${g},${b},${(opacity*0.5).toFixed(3)})`;
  ctx.lineWidth = 0.6;
  ctx.shadowBlur = 0;
  ctx.stroke();
  ctx.restore();
}

export function renderLotus(canvas, openness, rotation, petalStates, color, amplitude = 0) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  const cx = W/2, cy = H/2;
  // Mic amplitude drives glow intensity — stronger breath = brighter petals
  const ampGlow = Math.min(amplitude * 0.8, 0.6);
  for (let i = 0; i < N_PETALS; i++) {
    const angle     = BASE_ANGLE + (i/N_PETALS)*Math.PI*2 + rotation;
    const petalOpen = petalStates ? petalStates[i] : openness;
    const len       = CLOSED_LEN + (OPEN_LEN - CLOSED_LEN)*petalOpen;
    const wid       = CLOSED_W   + (OPEN_W   - CLOSED_W  )*petalOpen;
    const opacity   = 0.18 + 0.82*petalOpen;
    const glow      = petalOpen * 0.7 + ampGlow;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    drawPetal(ctx, len, wid, opacity, glow, color);
    ctx.restore();
  }
  const r2 = parseInt(color.slice(1,3),16);
  const g2 = parseInt(color.slice(3,5),16);
  const b2 = parseInt(color.slice(5,7),16);
  // Centre dot pulses with amplitude — more noticeable response
  const dotR = 2.5 + openness*1.5 + amplitude*3.5;
  ctx.beginPath();
  ctx.arc(cx, cy, dotR, 0, Math.PI*2);
  ctx.fillStyle = `rgba(${r2},${g2},${b2},${(0.4+openness*0.6).toFixed(2)})`;
  ctx.shadowColor = color;
  ctx.shadowBlur  = openness * 10 + amplitude * 14;
  ctx.fill();
  ctx.shadowBlur  = 0;
}

/** Step timing config — inhale ms / exhale ms */
export const STEP_TIMINGS = {
  kapalbhati:    { inMs: 600,  exMs: 600  },
  bhramari:      { inMs: 3000, exMs: 6000 },
  rechaka:       { inMs: 4000, exMs: 8000 },
  nadi_shodhana: { inMs: 4000, exMs: 4000 },
};
